import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import type { MeResponse } from '@inventohub/shared';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';

const DENIED_MESSAGES: Record<string, string> = {
  unknown: 'That Google account is not on InventoHub. Ask an admin to add you.',
  inactive: 'Your InventoHub account is inactive. Ask an admin to restore access.',
  error: 'Google sign-in failed. Try again, or ask an admin for help.',
};

export function LoginPage(): JSX.Element {
  const { user, code, loading, refresh } = useAuth();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo') ?? params.get('return_to'));
  const denied = params.get('denied');
  const [eventCode, setEventCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (loading) {
    return (
      <section className="card" data-testid="login-page">
        <p className="meta">Checking sign-in…</p>
      </section>
    );
  }

  if (user || code) {
    return <Navigate to={returnTo} replace />;
  }

  const googleHref = `/api/auth/google?return_to=${encodeURIComponent(returnTo)}`;
  const message = denied ? (DENIED_MESSAGES[denied] ?? DENIED_MESSAGES.error) : null;

  async function onCodeSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setCodeError(null);
    setBusy(true);
    try {
      await apiRequest<MeResponse>('/api/auth/code', {
        method: 'POST',
        body: { code: eventCode },
      });
      await refresh();
    } catch (caught: unknown) {
      setCodeError(
        caught instanceof ApiClientError ? caught.message : 'That event code is not valid.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" data-testid="login-page">
      <h2>Sign in</h2>
      <p>
        Staff sign in with the Google account an admin has provisioned. There is no public signup.
      </p>
      {message ? (
        <p className="status status--error" data-testid="login-denied">
          {message}
        </p>
      ) : null}
      <a className="button" href={googleHref} data-testid="google-sign-in">
        Sign in with Google
      </a>

      <h2>Tournament director</h2>
      <p className="meta">
        Use the named event code an equipment admin issued. Staff Google users do not need a code.
      </p>
      {codeError ? (
        <p className="status status--error" data-testid="event-code-error">
          {codeError}
        </p>
      ) : null}
      <form onSubmit={(event) => void onCodeSubmit(event)} data-testid="event-code-form">
        <label className="field">
          Event code
          <input
            value={eventCode}
            onChange={(event) => setEventCode(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            data-testid="event-code-input"
          />
        </label>
        <button
          type="submit"
          className="button"
          disabled={busy || eventCode.trim().length === 0}
          data-testid="event-code-submit"
        >
          Continue with event code
        </button>
      </form>
      <p className="meta">
        <Link to="/help/sign-in" data-testid="login-help-sign-in">
          How to sign in
        </Link>
      </p>
    </section>
  );
}

function safeReturnTo(value: string | null): string {
  if (!value) return '/';
  let candidate: string;
  try {
    candidate = decodeURIComponent(value);
  } catch {
    return '/';
  }
  if (!candidate.startsWith('/')) return '/';
  if (candidate.startsWith('//') || candidate.includes('://') || candidate.includes('\\')) {
    return '/';
  }
  return candidate;
}
